import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "common/components";
import "./SearchShimmer.scss";

interface SearchShimmerProps {
  query: string;
  error?: string | null;
}

const TOTAL_SECONDS = 30;

const STAGES = [
  { label: "Understanding your question…", icon: "🔍", duration: 5 },
  {
    label: "Retrieving relevant engineering context…",
    icon: "📚",
    duration: 6
  },
  { label: "Analyzing related documents…", icon: "📄", duration: 7 },
  { label: "Extracting applicable requirements…", icon: "⚙️", duration: 6 },
  { label: "Synthesizing a validated response…", icon: "✨", duration: 6 }
];

const SearchShimmer: FC<SearchShimmerProps> = ({ query, error }) => {
  const navigate = useNavigate();
  const [activeStage, setActiveStage] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  // Elapsed time tracker
  useEffect(() => {
    if (error) return;
    const interval = setInterval(() => {
      setSecondsElapsed((prev) =>
        prev < TOTAL_SECONDS ? prev + 1 : TOTAL_SECONDS
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [error]);

  // Stage progression based on elapsed time
  useEffect(() => {
    if (error) return;
    let cumulative = 0;
    for (let i = 0; i < STAGES.length; i++) {
      cumulative += STAGES[i].duration;
      if (secondsElapsed < cumulative) {
        setActiveStage(i);
        return;
      }
    }
    setActiveStage(STAGES.length - 1);
  }, [secondsElapsed, error]);

  const progressPercent = (secondsElapsed / TOTAL_SECONDS) * 100;

  if (error) {
    return (
      <div className="search-shimmer">
        <div className="search-shimmer__error">
          <div className="search-shimmer__error-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#FDECEA" />
              <path
                d="M24 16V26M24 30H24.02"
                stroke="#D32F2F"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="search-shimmer__error-title">Something went wrong</h2>
          <p className="search-shimmer__error-subtitle">{error}</p>
          <Button onClick={() => navigate("/")}>Back to Search</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="search-shimmer">
      <div className="search-shimmer__card">
        <div className="search-shimmer__header">
          <div className="search-shimmer__header-left">
            <div className="search-shimmer__pulse-ring">
              <span className="search-shimmer__status-dot" />
            </div>
            <div>
              <h2 className="search-shimmer__heading">AI Agent Processing</h2>
              {query && (
                <p className="search-shimmer__query">&ldquo;{query}&rdquo;</p>
              )}
            </div>
          </div>
        </div>

        <div className="search-shimmer__progress">
          <div
            className="search-shimmer__progress-fill"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        <div className="search-shimmer__steps">
          {STAGES.map((stage, i) => {
            const isDone = i < activeStage;
            const isActive = i === activeStage;
            return (
              <div
                key={i}
                className={`search-shimmer__step ${
                  isDone
                    ? "search-shimmer__step--done"
                    : isActive
                    ? "search-shimmer__step--active"
                    : ""
                }`}
              >
                <div className="search-shimmer__step-icon">
                  {isDone ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <defs>
                        <linearGradient
                          id="checkGrad"
                          x1="0"
                          y1="0"
                          x2="20"
                          y2="20"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#4F7BF7" />
                          <stop offset="100%" stopColor="#E84FAD" />
                        </linearGradient>
                      </defs>
                      <circle cx="10" cy="10" r="10" fill="url(#checkGrad)" />
                      <path
                        d="M6 10L9 13L14 7"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="search-shimmer__step-emoji">
                      {stage.icon}
                    </span>
                  )}
                </div>
                <div className="search-shimmer__step-content">
                  <span className="search-shimmer__step-label">
                    {stage.label}
                  </span>
                  {isActive && (
                    <div className="search-shimmer__step-bar">
                      <div className="search-shimmer__step-bar-fill" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SearchShimmer;
